import React, { useState, useRef, useEffect } from 'react';
import axiosInstance from '../axiosConfig';

const renderInlineText = (text, keyPrefix) => (
  text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyPrefix}-${index}`}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={`${keyPrefix}-${index}`}>{part}</React.Fragment>;
  })
);

const isTableDivider = (line) => /^\s*\|?(\s*:?-+:?\s*\|)+\s*:?-+:?\s*\|?\s*$/.test(line);
const getTableCells = (line) => line.trim().replace(/^\||\|$/g, '').split('|').map(cell => cell.trim());

const downloadTableAsCsv = (headers, rows) => {
  const escapeCsvCell = (value) => `"${String(value || '').replace(/"/g, '""')}"`;
  const csv = [headers, ...rows]
    .map(row => row.map(escapeCsvCell).join(','))
    .join('\r\n');
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `wip-ai-table-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

function MarkdownMessage({ content }) {
  const lines = String(content || '').split('\n');
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index].trim();

    if (!line) {
      index += 1;
      continue;
    }

    if (line.includes('|') && index + 1 < lines.length && isTableDivider(lines[index + 1])) {
      const headers = getTableCells(line);
      const rows = [];
      index += 2;

      while (index < lines.length && lines[index].includes('|') && lines[index].trim()) {
        rows.push(getTableCells(lines[index]));
        index += 1;
      }

      blocks.push(
        <div className="ai-message-table-block" key={`table-${index}`}>
          <div className="ai-message-table-toolbar">
            <span>{rows.length} rows</span>
            <button type="button" onClick={() => downloadTableAsCsv(headers, rows)}>
              <i className="bi bi-download"></i>
              Download CSV
            </button>
          </div>
          <div className="ai-message-table-wrap">
            <table className="ai-message-table">
              <thead>
                <tr>{headers.map((header, cellIndex) => <th key={cellIndex}>{renderInlineText(header, `th-${cellIndex}`)}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {headers.map((_, cellIndex) => <td key={cellIndex}>{renderInlineText(row[cellIndex] || '', `td-${rowIndex}-${cellIndex}`)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
      continue;
    }

    const listMatch = line.match(/^[-*]\s+(.+)$/) || line.match(/^\d+\.\s+(.+)$/);
    if (listMatch) {
      const ordered = /^\d+\./.test(line);
      const items = [];

      while (index < lines.length) {
        const current = lines[index].trim();
        const match = ordered ? current.match(/^\d+\.\s+(.+)$/) : current.match(/^[-*]\s+(.+)$/);
        if (!match) break;
        items.push(match[1]);
        index += 1;
      }

      const ListTag = ordered ? 'ol' : 'ul';
      blocks.push(
        <ListTag className="ai-message-list" key={`list-${index}`}>
          {items.map((item, itemIndex) => <li key={itemIndex}>{renderInlineText(item, `li-${itemIndex}`)}</li>)}
        </ListTag>
      );
      continue;
    }

    blocks.push(<p className="ai-message-paragraph" key={`p-${index}`}>{renderInlineText(line, `p-${index}`)}</p>);
    index += 1;
  }

  return <div className="ai-markdown-message">{blocks}</div>;
}

export default function AiChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hello! I am your WIP AI Assistant. Ask me anything about tickets, supervisors, or workers!' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);
  const [speechError, setSpeechError] = useState('');
  const chatScrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const speechBaseInputRef = useRef('');

  const toggleChat = () => setIsOpen(!isOpen);

  const scrollToBottom = () => {
    const container = chatScrollRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSpeechSupported(false);
      return undefined;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setSpeechError('');
    };

    recognition.onresult = (event) => {
      let transcript = '';
      for (let resultIndex = 0; resultIndex < event.results.length; resultIndex += 1) {
        transcript += event.results[resultIndex][0].transcript;
      }
      const prefix = speechBaseInputRef.current.trim();
      setInput(`${prefix}${prefix && transcript ? ' ' : ''}${transcript}`.trimStart());
    };

    recognition.onerror = (event) => {
      const messagesByError = {
        'not-allowed': 'Microphone permission was denied. Please allow microphone access.',
        'audio-capture': 'No microphone was detected.',
        'no-speech': 'No speech was detected. Please try again.',
        network: 'Speech recognition network error. Please try again.'
      };
      setSpeechError(messagesByError[event.error] || 'Speech recognition failed. Please try again.');
      setIsListening(false);
    };

    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;

    return () => {
      recognition.onstart = null;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.abort();
      recognitionRef.current = null;
    };
  }, []);

  const toggleSpeechRecognition = () => {
    const recognition = recognitionRef.current;
    if (!recognition || isLoading) return;

    if (isListening) {
      recognition.stop();
      return;
    }

    speechBaseInputRef.current = input;
    recognition.lang = 'en-IN';
    setSpeechError('');

    try {
      recognition.start();
    } catch (_) {
      setSpeechError('Microphone is already starting. Please try again.');
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
    }

    const newMessages = [...messages, { role: 'user', content: input }];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      // Filter out 'tool' messages if we only want to send the conversation history to our simple backend
      // But our backend actually expects the full conversation including tool messages to work properly.
      // For simplicity, we just send user and assistant messages for context
      const contextMessages = newMessages.filter(m => m.role === 'user' || m.role === 'assistant');

      const res = await axiosInstance.post('/api/ai/chat', { messages: contextMessages });
      
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.message }]);
    } catch (error) {
      console.error('Chat error:', error);
      const status = error.response?.status ? ` (HTTP ${error.response.status})` : '';
      const networkReason = error.message ? ` ${error.message}` : '';
      let errorMsg = `Sorry, there was an error connecting to the AI server${status}.${networkReason}`;
      const apiReason = error.response?.data?.error || error.response?.data?.message;
      if (apiReason) {
          errorMsg += ' Reason: ' + apiReason;
      }
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') sendMessage();
  };

  return (
    <>
      <style>
        {`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes messagePopup {
            from { opacity: 0; transform: translateY(10px) scale(0.98); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          .ai-chat-scroll::-webkit-scrollbar {
            width: 9px;
          }
          .ai-chat-scroll::-webkit-scrollbar-track {
            background: #edf1f7;
            border-radius: 999px;
          }
          .ai-chat-scroll::-webkit-scrollbar-thumb {
            background: #9aa8c4;
            border: 2px solid #edf1f7;
            border-radius: 999px;
          }
          .ai-chat-scroll::-webkit-scrollbar-thumb:hover {
            background: #64748b;
          }
          .ai-chat-window {
            width: min(720px, calc(100vw - 32px)) !important;
            height: min(720px, calc(100dvh - 32px)) !important;
            max-height: calc(100dvh - 32px);
          }
          .ai-chat-header,
          .ai-chat-composer {
            flex: 0 0 auto;
          }
          .ai-chat-input::placeholder {
            color: #94a3b8;
          }
          .ai-chat-input:focus {
            border-color: #4f67e8 !important;
            background: #ffffff !important;
            box-shadow: 0 0 0 3px rgba(79, 103, 232, 0.12);
          }
          .ai-speech-button {
            width: 48px;
            min-width: 48px;
            height: 48px;
            border: 1px solid #dbe3ef;
            border-radius: 15px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s;
          }
          .ai-speech-button.listening {
            color: #ffffff;
            background: #dc2626;
            border-color: #dc2626;
            box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.14);
            animation: aiMicPulse 1.2s ease-in-out infinite;
          }
          @keyframes aiMicPulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.06); }
          }
          .ai-markdown-message {
            min-width: 0;
          }
          .ai-message-paragraph {
            margin: 0 0 10px;
          }
          .ai-message-paragraph:last-child {
            margin-bottom: 0;
          }
          .ai-message-list {
            margin: 6px 0 10px;
            padding-left: 20px;
          }
          .ai-message-table-block {
            width: 100%;
            min-width: 0;
            margin: 8px 0 10px;
          }
          .ai-message-table-toolbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            margin-bottom: 7px;
            color: #64748b;
            font-size: 12px;
          }
          .ai-message-table-toolbar button {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 10px;
            border: 1px solid #c7d2fe;
            border-radius: 8px;
            background: #eef2ff;
            color: #3346b5;
            font-size: 12px;
            font-weight: 700;
            cursor: pointer;
          }
          .ai-message-table-toolbar button:hover {
            background: #e0e7ff;
          }
          .ai-message-table-wrap {
            width: 100%;
            overflow-x: auto;
            direction: ltr;
            scrollbar-gutter: stable;
            overscroll-behavior-x: contain;
            border: 1px solid #dbe3ef;
            border-radius: 8px;
          }
          .ai-message-table {
            width: max-content;
            min-width: 100%;
            border-collapse: collapse;
            font-size: 13px;
            background: #ffffff;
          }
          .ai-message-table th,
          .ai-message-table td {
            padding: 8px 10px;
            border-bottom: 1px solid #e2e8f0;
            text-align: left;
            vertical-align: top;
            white-space: nowrap;
          }
          .ai-message-table th {
            background: #eff6ff;
            color: #1e3a8a;
            font-weight: 700;
          }
          .ai-message-table th:first-child,
          .ai-message-table td:first-child {
            position: sticky;
            left: 0;
            z-index: 1;
            background: #ffffff;
          }
          .ai-message-table th:first-child {
            z-index: 2;
            background: #eff6ff;
          }
          .ai-message-table tr:last-child td {
            border-bottom: none;
          }
          @media (max-width: 768px) {
            .ai-chat-window {
              right: 10px !important;
              bottom: 10px !important;
              width: calc(100vw - 20px) !important;
              height: calc(100dvh - 20px) !important;
              max-height: calc(100dvh - 20px) !important;
              border-radius: 18px !important;
            }
            .ai-chat-scroll {
              padding: 18px !important;
            }
          }
          @media (max-width: 480px) {
            .ai-chat-window {
              right: 0 !important;
              bottom: 0 !important;
              width: 100vw !important;
              height: 100dvh !important;
              max-height: 100dvh !important;
              border: 0 !important;
              border-radius: 0 !important;
            }
            .ai-chat-header {
              padding: 15px 16px !important;
            }
            .ai-chat-scroll {
              padding: 14px !important;
            }
            .ai-chat-composer {
              padding: 12px !important;
            }
          }
          @media (max-height: 600px) {
            .ai-chat-window {
              bottom: 8px !important;
              height: calc(100dvh - 16px) !important;
              max-height: calc(100dvh - 16px) !important;
            }
            .ai-chat-header {
              padding: 12px 16px !important;
            }
            .ai-chat-scroll {
              padding-top: 14px !important;
              padding-bottom: 14px !important;
            }
            .ai-chat-composer {
              padding-top: 10px !important;
              padding-bottom: 10px !important;
            }
          }
        `}
      </style>

      {/* Floating Button (Hidden when open) */}
      <div 
        onClick={toggleChat}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '60px',
          height: '60px',
          backgroundColor: '#1d4ed8',
          color: 'white',
          borderRadius: '50%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0 8px 24px rgba(29, 78, 216, 0.4)',
          cursor: 'pointer',
          zIndex: 9999,
          fontSize: '26px',
          transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          opacity: isOpen ? 0 : 1,
          transform: isOpen ? 'scale(0.5) translateY(20px)' : 'scale(1) translateY(0)',
          pointerEvents: isOpen ? 'none' : 'auto'
        }}
      >
        <i className="bi bi-chat-dots-fill"></i>
      </div>

      {/* Chat Window */}
      <div
        className="ai-chat-window"
        style={{
          position: 'fixed',
          bottom: isOpen ? '20px' : '-20px',
          right: '20px',
          width: '720px',
          height: '720px',
          backgroundColor: '#ffffff',
          borderRadius: '22px',
          boxShadow: '0 24px 70px -18px rgba(15, 23, 42, 0.35)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 10000,
          transition: 'all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          overflow: 'hidden',
          border: '1px solid rgba(148, 163, 184, 0.35)'
        }}
      >
        {/* Header */}
        <div className="ai-chat-header" style={{
          background: 'linear-gradient(135deg, #25358f 0%, #4964e8 100%)',
          color: 'white',
          padding: '20px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ 
              width: '46px', 
              height: '46px', 
              borderRadius: '14px', 
              backgroundColor: 'rgba(255,255,255,0.96)', 
              color: '#4055cf',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <i className="bi bi-robot fs-5"></i>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '17px', letterSpacing: '0.2px' }}>WIP AI Assistant</div>
              <div style={{ fontSize: '12.5px', opacity: 0.9, marginTop: '3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#86efac', boxShadow: '0 0 0 3px rgba(134,239,172,.18)' }}></span>
                Live factory insights
              </div>
            </div>
          </div>
          <button onClick={toggleChat} style={{ 
            background: 'transparent', 
            border: 'none', 
            color: 'rgba(255,255,255,0.8)', 
            cursor: 'pointer',
            width: '38px',
            height: '38px',
            borderRadius: '10px',
            padding: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.8)'}
          >
            <i className="bi bi-chevron-down fs-5"></i>
          </button>
        </div>

        {/* Messages */}
        <div ref={chatScrollRef} className="ai-chat-scroll" style={{
          flex: 1,
          minHeight: 0,
          padding: '24px',
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'contain',
          scrollbarGutter: 'stable',
          scrollbarColor: '#9aa8c4 #edf1f7',
          background: 'linear-gradient(180deg, #f8faff 0%, #f4f7fb 100%)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          {messages.map((msg, idx) => (
            <div key={idx} style={{
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              backgroundColor: msg.role === 'user' ? '#2563eb' : '#ffffff',
              color: msg.role === 'user' ? 'white' : '#1e293b',
              padding: msg.role === 'user' ? '13px 17px' : '15px 17px',
              borderRadius: msg.role === 'user' ? '18px 18px 5px 18px' : '18px 18px 18px 5px',
              maxWidth: msg.role === 'user' ? '82%' : '92%',
              border: msg.role === 'assistant' ? '1px solid #e2e8f0' : 'none',
              boxShadow: msg.role === 'user' ? '0 7px 18px rgba(66, 84, 209, 0.22)' : '0 5px 18px rgba(15, 23, 42, 0.06)',
              fontSize: '14px',
              whiteSpace: msg.role === 'user' ? 'pre-wrap' : 'normal',
              lineHeight: '1.5',
              minWidth: 0,
              flexShrink: 0,
              animation: 'messagePopup 0.3s ease-out forwards'
            }}>
              {msg.role === 'assistant' ? <MarkdownMessage content={msg.content} /> : msg.content}
            </div>
          ))}
          {isLoading && (
            <div style={{ 
              alignSelf: 'flex-start', 
              backgroundColor: '#ffffff', 
              color: '#64748b', 
              padding: '12px 16px', 
              borderRadius: '16px 16px 16px 4px', 
              border: '1px solid #e2e8f0', 
              fontSize: '14px',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)',
              display: 'flex',
              flexShrink: 0,
              alignItems: 'center',
              gap: '8px',
              animation: 'messagePopup 0.3s ease-out forwards'
            }}>
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span style={{marginLeft: '4px'}}>Thinking...</span>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="ai-chat-composer" style={{
          padding: '16px 18px 18px',
          backgroundColor: '#ffffff',
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          gap: '7px'
        }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', width: '100%' }}>
            <input
              className="ai-chat-input"
              type="text"
              placeholder={isListening ? 'Listening...' : 'Type or speak your message...'}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isLoading}
              style={{
                flex: 1,
                minWidth: 0,
                height: '48px',
                padding: '0 18px',
                border: isListening ? '1px solid #ef4444' : '1px solid #e2e8f0',
                borderRadius: '15px',
                outline: 'none',
                fontSize: '14px',
                backgroundColor: '#f8fafc',
                transition: 'border-color 0.2s, background-color 0.2s, box-shadow 0.2s'
              }}
            />
            <button
              type="button"
              className={`ai-speech-button${isListening ? ' listening' : ''}`}
              onClick={toggleSpeechRecognition}
              disabled={!speechSupported || isLoading}
              title={!speechSupported ? 'Speech recognition is not supported in this browser' : isListening ? 'Stop listening' : 'Start voice input'}
              style={{
                background: isListening ? undefined : '#f8fafc',
                color: isListening ? undefined : speechSupported ? '#4055cf' : '#94a3b8',
                cursor: speechSupported && !isLoading ? 'pointer' : 'default'
              }}
            >
              <i className={`bi ${isListening ? 'bi-stop-fill' : 'bi-mic-fill'}`} style={{ fontSize: '18px' }}></i>
            </button>
            <button 
              onClick={sendMessage}
              disabled={isLoading || !input.trim()}
              style={{
                width: '48px',
                minWidth: '48px',
                height: '48px',
                borderRadius: '15px',
                background: input.trim() && !isLoading ? 'linear-gradient(135deg, #4055cf, #5c73ee)' : '#e8edf5',
                color: input.trim() && !isLoading ? 'white' : '#94a3b8',
                border: 'none',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: input.trim() && !isLoading ? 'pointer' : 'default',
                boxShadow: input.trim() && !isLoading ? '0 7px 16px rgba(64, 85, 207, 0.28)' : 'none',
                transition: 'all 0.2s',
                transform: input.trim() && !isLoading ? 'scale(1)' : 'scale(0.95)'
              }}
            >
              <i className="bi bi-send-fill" style={{ fontSize: '16px', marginLeft: '2px' }}></i>
            </button>
          </div>
          {(isListening || speechError || !speechSupported) && (
            <div style={{ width: '100%', fontSize: '12px', color: speechError ? '#dc2626' : '#64748b', paddingLeft: '3px' }}>
              {speechError || (!speechSupported
                ? 'Voice input is not supported in this browser. Please use Chrome or Edge.'
                : 'Listening... Speak in Hinglish or English.')}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
